// src/routes/MediaSelection/RequirementsView.js

import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Divider, Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, Accordion, AccordionSummary, AccordionDetails, Tooltip, Zoom } from '@mui/material';
import { motion } from 'framer-motion';
import axios from 'axios';
import { showError } from '../../utils/errorHandler';
import { useTheme } from '@mui/material/styles';
import { accentBorderCard, designTokens } from '../../theme/designTokens';
import { bg, adaptiveColor } from '../../utils/darkModeAdapter';

// MUI Icons
import EditNoteIcon from '@mui/icons-material/EditNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PlaceIcon from '@mui/icons-material/Place';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import DateRangeIcon from '@mui/icons-material/DateRange';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import UpdateIcon from '@mui/icons-material/Update';

/**
 * [RequirementsView]
 *   - 先ほどの修正に合わせて、
 *     必須項目: position, targetHires, advertisingBudget, location
 *     任意項目: budgetUpperLimit, targetCandidateGroup, preferredMedia,
 *               adDuration, previousMediaUsage
 */
export default function RequirementsView({ requirements, onRequirementsUpdated, onLoadingStateChange }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({...requirements});
  const [optionalOpen, setOptionalOpen] = useState(false);
  
  // ローディング状態の変更を親に伝える
  useEffect(() => {
    if (onLoadingStateChange) {
      onLoadingStateChange(isLoading);
    }
  }, [isLoading, onLoadingStateChange]);

  // 関数: 円表記を万円表記に変換
  const convertToManEn = (value) => {
    if (!value) return '未入力';
    // 数値に変換できる場合のみ処理（例: "50000" -> 5万円）
    const num = parseInt(value, 10);
    if (isNaN(num)) return value; // 数値変換できなければそのまま返す
    return `${Math.floor(num / 10000)} 万円`;
  };

  // 必須項目が未入力なら赤背景にする
  const requiredFieldStyle = (val) => {
    const strVal = typeof val === 'string' ? val.trim() : '';
    return {
      backgroundColor: strVal === '' ? adaptiveColor(muiTheme, '#fff8f8', 'rgba(60,30,30,0.3)') : bg(muiTheme).card
    };
  };

  // 入力ハンドラ
  const handleChange = (e) => {
    let { name, value } = e.target;
    if (['targetHires', 'advertisingBudget', 'budgetUpperLimit'].includes(name)) {
      // 数字以外を除去
      value = value.replace(/\D/g, '');
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 送信可否チェック
  const canSubmit = () => {
    const isEmpty = (v) => !v || !v.trim();
    // 必須5項目が埋まっているか
    if (
      isEmpty(formData.position) ||
      isEmpty(formData.targetHires) ||
      isEmpty(formData.advertisingBudget) ||
      isEmpty(formData.location) ||
      isEmpty(formData.desiredCompletionDate)
    ) {
      return false;
    }
    return true;
  };

  // ダイアログを開く
  const handleOpenDialog = () => {
    setFormData({...requirements});
    setDialogOpen(true);
  };

  // ダイアログを閉じる
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // 再選定を実行
  const handleRegenerate = async () => {
    if (!canSubmit()) {
      alert("必須項目（職種／希望人数／媒体広告費／勤務地／採用完了希望日）が未入力です。");
      return;
    }

    if (!requirements.resultId) {
      alert("結果IDが見つかりません。先に媒体選定を実行してください。");
      return;
    }

    setIsLoading(true);
    setDialogOpen(false); // 先にダイアログを閉じる
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      
      const response = await axios.post(`${API_BASE_URL}/media_selection/regenerate_selection`, {
        resultId: requirements.resultId,
        userMessage: `以下の条件で再選定してください：
職種: ${formData.position}
希望採用人数: ${formData.targetHires}
媒体広告費: ${formData.advertisingBudget}
勤務地: ${formData.location}
採用完了希望日: ${formData.desiredCompletionDate}
採用予算上限: ${formData.budgetUpperLimit || ''}
ターゲット人材層: ${formData.targetCandidateGroup || ''}
希望媒体: ${formData.preferredMedia || ''}
掲載期間: ${formData.adDuration || ''}
過去の利用媒体: ${formData.previousMediaUsage || ''}`
      }, {
        withCredentials: true,
        timeout: 120000  // 120秒タイムアウト
      });

      // 更新されたデータを親コンポーネントに渡す
      if (onRequirementsUpdated && response.data) {
        onRequirementsUpdated(response.data, formData);
      }
    } catch (error) {
      console.error('Error regenerating media selection:', error);
      if (error.code === 'ECONNABORTED') {
        showError(error, '媒体選定の再生成がタイムアウトしました。もう一度お試しください。');
      } else if (error.response?.status === 422) {
        showError(error, '入力データに不備があります\n\n必要な情報を確認してから再度お試しください');
      } else {
        showError(error, '媒体選定の再生成中にエラーが発生しました');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const muiTheme = useTheme();
  const theme = designTokens.featureThemes.media_selection;

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      elevation={0}
      sx={{
        ...accentBorderCard('media_selection', muiTheme),
        p: 3,
      }}
    >
      {/* タイトルと再選定ボタン */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: theme.primary,
          }}
        >
          入力内容の確認
        </Typography>
        <Tooltip 
          title="内容を修正して媒体選定を再生成します" 
          placement="top" 
          arrow
          TransitionComponent={Zoom}
        >
          <Button
            variant="contained"
            startIcon={<UpdateIcon />}
            onClick={handleOpenDialog}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              fontWeight: 'bold',
              color: '#ffffff',
              background: 'linear-gradient(90deg, #4e7df3, #21CBF3)',
              boxShadow: '0 3px 8px rgba(0,0,0,0.2)',
              px: 2,
              py: 1,
              '&:hover': {
                opacity: 0.9,
                boxShadow: '0 6px 12px rgba(0,0,0,0.3)',
                background: 'linear-gradient(90deg, #4e7df3, #21CBF3)'
              },
              transition: 'all 0.2s'
            }}
          >
            内容を修正して再選定
          </Button>
        </Tooltip>
      </Box>

      {/* ============ 必須項目 ============ */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
        viewport={{ once: true }}
        sx={{ mb: 2, p: 2, borderRadius: 2, backgroundColor: bg(muiTheme).card }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>
          【必須項目】
        </Typography>
        
        <Grid container spacing={2}>
          {/* 職種 */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WorkIcon sx={{ color: theme.primary }} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                職種:
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary' }}>
              {requirements.position || '未入力'}
            </Typography>
          </Grid>

          {/* 希望採用人数 */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon sx={{ color: theme.primary }} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                希望採用人数:
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary' }}>
              {requirements.targetHires || '未入力'}
            </Typography>
          </Grid>

          {/* 媒体広告費 */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoneyIcon sx={{ color: theme.primary }} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                媒体広告費:
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary' }}>
              {requirements.advertisingBudget
                ? `${convertToManEn(requirements.advertisingBudget)} `
                : '未入力'}
            </Typography>
          </Grid>

          {/* 勤務地 */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PlaceIcon sx={{ color: theme.primary }} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                勤務地:
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary' }}>
              {requirements.location || '未入力'}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* ============ 任意項目 ============ */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
        viewport={{ once: true }}
        sx={{ p: 2, borderRadius: 2, backgroundColor: bg(muiTheme).card }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>
          【任意項目】
        </Typography>
        
        <Grid container spacing={2}>
          {/* 採用予算上限 */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MonetizationOnIcon sx={{ color: theme.primary }} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                採用予算上限:
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary' }}>
              {requirements.budgetUpperLimit
                ? `${convertToManEn(requirements.budgetUpperLimit)} `
                : '未入力'}
            </Typography>
          </Grid>

          {/* ターゲット人材層 */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupWorkIcon sx={{ color: theme.primary }} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                ターゲット人材層:
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary' }}>
              {requirements.targetCandidateGroup || '未入力'}
            </Typography>
          </Grid>

          {/* 希望する募集媒体 */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WebAssetIcon sx={{ color: theme.primary }} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                希望する募集媒体:
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary' }}>
              {requirements.preferredMedia || '未入力'}
            </Typography>
          </Grid>

          {/* 希望広告掲載期間 */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DateRangeIcon sx={{ color: theme.primary }} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                希望広告掲載期間:
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary' }}>
              {requirements.adDuration || '未入力'}
            </Typography>
          </Grid>

          {/* 過去に使用した媒体/運用実績 */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryEduIcon sx={{ color: theme.primary }} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                過去に使用した媒体・運用実績:
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
              {requirements.previousMediaUsage || '未入力'}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* 編集ダイアログ */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{
            fontWeight: 600,
            color: '#249BE0',
          }}>
            媒体選定条件の編集
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {/* ダイアログ内のフォーム */}
          <Box component="form" noValidate>
            {/* ------ 必須項目 ------ */}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, mt: 1, color: 'text.secondary' }}>
              必須項目
            </Typography>
            <TextField
              label="職種 (必須)"
              name="position"
              fullWidth
              sx={{ mb: 2, 
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#4e7df3',
                  },
                },
              }}
              InputProps={{
                style: requiredFieldStyle(formData.position)
              }}
              value={formData.position || ''}
              onChange={handleChange}
            />

            <TextField
              label="希望採用人数 (必須)"
              name="targetHires"
              type="text"
              fullWidth
              sx={{ mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#4e7df3',
                  },
                },
              }}
              InputProps={{
                style: requiredFieldStyle(formData.targetHires)
              }}
              value={formData.targetHires || ''}
              onChange={handleChange}
              helperText="※ 半角数字のみ入力"
            />

            <TextField
              label="媒体広告費（円）(必須)"
              name="advertisingBudget"
              type="text"
              fullWidth
              sx={{ mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#4e7df3',
                  },
                },
              }}
              InputProps={{
                style: requiredFieldStyle(formData.advertisingBudget)
              }}
              value={formData.advertisingBudget || ''}
              onChange={handleChange}
              helperText="※ 半角数字のみ入力（円単位）"
            />

            <TextField
              label="勤務地 (必須)"
              name="location"
              fullWidth
              sx={{ mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#4e7df3',
                  },
                },
              }}
              InputProps={{
                style: requiredFieldStyle(formData.location)
              }}
              value={formData.location || ''}
              onChange={handleChange}
            />

            <TextField
              label="採用完了希望日 (必須)"
              name="desiredCompletionDate"
              type="date"
              fullWidth
              sx={{ mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#4e7df3',
                  },
                },
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                style: requiredFieldStyle(formData.desiredCompletionDate)
              }}
              value={formData.desiredCompletionDate || ''}
              onChange={handleChange}
            />

            {/* ------ 任意入力項目: Accordionで隠す ------ */}
            <Accordion
              expanded={optionalOpen}
              onChange={() => setOptionalOpen(!optionalOpen)}
              sx={{ mt: 3, backgroundColor: bg(muiTheme).subtle, boxShadow: 'none', borderRadius: 2 }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: '#4e7df3' }} />}
                sx={{ 
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(78, 125, 243, 0.04)'
                  }
                }}
              >
                <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                  {optionalOpen ? '任意入力項目(クリックで閉じる)' : '任意入力項目(クリックで開く)'}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  label="採用予算の上限（円）"
                  name="budgetUpperLimit"
                  type="text"
                  fullWidth
                  sx={{ mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#4e7df3',
                      },
                    },
                  }}
                  value={formData.budgetUpperLimit || ''}
                  onChange={handleChange}
                />

                <TextField
                  label="ターゲット人材層（新卒、中途、若手、シニアなど）"
                  name="targetCandidateGroup"
                  fullWidth
                  sx={{ mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#4e7df3',
                      },
                    },
                  }}
                  value={formData.targetCandidateGroup || ''}
                  onChange={handleChange}
                />

                <TextField
                  label="希望する媒体（複数可）"
                  name="preferredMedia"
                  fullWidth
                  sx={{ mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#4e7df3',
                      },
                    },
                  }}
                  value={formData.preferredMedia || ''}
                  onChange={handleChange}
                  helperText="例：リクナビ, Green, Wantedly など"
                />

                <TextField
                  label="広告掲載期間（例：2ヶ月など）"
                  name="adDuration"
                  fullWidth
                  sx={{ mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#4e7df3',
                      },
                    },
                  }}
                  value={formData.adDuration || ''}
                  onChange={handleChange}
                />

                <TextField
                  label="過去に使用した媒体や運用実績"
                  name="previousMediaUsage"
                  fullWidth
                  multiline
                  minRows={2}
                  sx={{ mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#4e7df3',
                      },
                    },
                  }}
                  value={formData.previousMediaUsage || ''}
                  onChange={handleChange}
                />
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={handleCloseDialog} 
            sx={{ 
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(69, 90, 121, 0.08)'
              }
            }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleRegenerate}
            disabled={isLoading || !canSubmit()}
            startIcon={isLoading ? <CircularProgress size={24} color="inherit" /> : <UpdateIcon />}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              fontWeight: 'bold',
              color: '#ffffff',
              background: 'linear-gradient(90deg, #4e7df3, #21CBF3)',
              boxShadow: '0 3px 8px rgba(0,0,0,0.2)',
              px: 3,
              py: 1,
              '&:hover': {
                opacity: 0.9,
                boxShadow: '0 6px 12px rgba(0,0,0,0.3)',
                background: 'linear-gradient(90deg, #4e7df3, #21CBF3)'
              },
              transition: 'all 0.2s'
            }}
          >
            {isLoading ? '選定中...' : '再選定する'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}